import { useEffect } from "react"
import { useLocation } from "react-router"
import { simplePost } from "@/lib/apiUtils"

/**
 * Page views and clicks for the superadmin usage panel (server: usage.routes.ts).
 * Batched and flushed every 15 s and when the tab is hidden; failures are dropped.
 */
type UsageEvent = { kind: "view" | "click"; path: string; label?: string }

const queue: UsageEvent[] = []
const FLUSH_MS = 15_000
const CLICKABLE = "button, a, [role=button], [role=tab], [role=menuitem], [role=option], [role=checkbox], [role=switch]"

/** Route with ids masked, so every expedient counts as the same page. */
export function routeOf(pathname: string): string {
	return pathname.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ":id").replace(/\/\d+(?=\/|$)/g, "/:n")
}

function flush() {
	if (queue.length === 0) return
	simplePost("/api/usage/events", { events: queue.splice(0, 100) }).catch(() => {})
}

export function useUsageTracking(enabled: boolean) {
	const { pathname } = useLocation()

	useEffect(() => {
		if (enabled) queue.push({ kind: "view", path: routeOf(pathname) })
	}, [enabled, pathname])

	useEffect(() => {
		if (!enabled) return
		const onClick = (e: MouseEvent) => {
			const el = (e.target as Element | null)?.closest?.(CLICKABLE)
			if (!el) return
			const label = (el.getAttribute("aria-label") || el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 80)
			if (label) queue.push({ kind: "click", path: routeOf(window.location.pathname), label })
		}
		const onHidden = () => document.visibilityState === "hidden" && flush()
		document.addEventListener("click", onClick, true)
		document.addEventListener("visibilitychange", onHidden)
		const timer = window.setInterval(flush, FLUSH_MS)
		return () => {
			document.removeEventListener("click", onClick, true)
			document.removeEventListener("visibilitychange", onHidden)
			window.clearInterval(timer)
			flush()
		}
	}, [enabled])
}
